import { Suspense } from 'react';
import type { Dayjs } from 'dayjs';
import GAgGrid from 'components/technical/grids/GAgGrid';
import { isNil } from 'lodash/fp';
import { formatNumber } from 'components/formatter.utils';
import AssetLabel from 'components/market/asset/AssetLabel';
import { IconVariant } from 'components/market/asset/cryptocurrencies/CryptocurrenciesData';
import type { PortfolioFilters } from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters';
import WarningMessage from 'components/technical/WarningMessage/WarningMessage';
import {
  IMultiLevelAttributionType,
  type IReturnsType,
  useBottomUpPerformanceAttributionSuspenseQuery,
} from 'generated/graphql.tsx';
import { type BottomUpRowData, flatTreeDataAddPathHierarchy } from './BottomUpAttribution.utils';
import { convertDateRangeToSinceToDate } from 'components/dateRange.utils';
import { createColumnToolDef } from 'components/technical/grids/agGrid.utils.tsx';
import Loader from 'components/technical/Loader/Loader.tsx';

type BottomUpAttributionReportProps = {
  portfolioFilters: PortfolioFilters;
  dateRange: [Dayjs, Dayjs] | null;
  classification: string;
  returnsType: IReturnsType;
  benchmark: null | { id: string };
};

const MAX_DATA_LEVELS = 6;
const reportGridHeight = '85vh';

const LevelNameOrAssetGroupRenderer = ({ data }: { data: BottomUpRowData }) => {
  if (data.name) return <span style={{ textTransform: 'capitalize' }}>{data.name}</span>;
  if (data.asset) return <AssetLabel asset={data.asset} wrap={false} size={IconVariant.MEDIUM} />;
  return null;
};

const BottomUpAttributionReportInner = ({
  portfolioFilters,
  dateRange,
  classification,
  returnsType,
  benchmark,
}: BottomUpAttributionReportProps) => {
  const { data } = useBottomUpPerformanceAttributionSuspenseQuery({
    variables: {
      input: {
        attributionType: IMultiLevelAttributionType.BottomUp,
        clusterType: classification,
        dateRange: convertDateRangeToSinceToDate(dateRange),
        returnsType,
        subAccountAssetFilter: portfolioFilters,
        benchmarkId: benchmark?.id,
      },
    },
  });

  const reportData = data.portfolio.multilevelPerformanceAttributions;

  if (reportData.levelNames.length > MAX_DATA_LEVELS) {
    return (
      <WarningMessage showCard>Data has too many levels, currently supported {MAX_DATA_LEVELS} levels.</WarningMessage>
    );
  }

  const rowData = flatTreeDataAddPathHierarchy(reportData.data);

  return (
    <GAgGrid<BottomUpRowData>
      rowData={rowData}
      getDataPath={(data) => data.path}
      treeData
      height={reportGridHeight}
      autoSizeStrategy={{ type: 'fitCellContents' }}
      onGridReady={(grid) => grid.api.expandAll()}
      pivotPanelShow="never"
      autoGroupColumnDef={{
        headerName: '',
        minWidth: 280,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: LevelNameOrAssetGroupRenderer,
        },
        pinned: 'left',
      }}
      columnDefs={reportData.columns.map((column, index) => ({
        headerName: column,
        valueGetter: (params) => (!isNil(params.data?.values[index]) ? 100 * params.data.values[index]! : null),
        valueFormatter: (params) => (!isNil(params.value) ? formatNumber(params.value) : ''),
        suppressMenu: true,
        type: ['numericColumn', 'extendedNumericColumn'],
      }))}
      sideBar={{
        toolPanels: [
          createColumnToolDef({
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          }),
        ],
      }}
    />
  );
};

const BottomUpAttributionReport = (props: BottomUpAttributionReportProps) => {
  return (
    <Suspense fallback={<Loader appearance="container" />}>
      <BottomUpAttributionReportInner {...props} />
    </Suspense>
  );
};

export default BottomUpAttributionReport;
